import { useState, type FormEvent } from 'react'
import { AlertTriangle, CheckCircle2 } from 'lucide-react'

import { Alert, AlertDescription, AlertTitle } from '@quebon/ui/components/alert'
import { Button } from '@quebon/ui/components/button'
import { Checkbox } from '@quebon/ui/components/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@quebon/ui/components/dialog'
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldTitle,
} from '@quebon/ui/components/field'
import { Input } from '@quebon/ui/components/input'
import { Spinner } from '@quebon/ui/components/spinner'
import { Textarea } from '@quebon/ui/components/textarea'

import type { AdminTextbookRequestView, TextbookRequestStatus } from '@/api/textbookRequests'
import {
  TEXTBOOK_STATUS_LABELS,
  textbookRequestBookLabel,
} from './textbookRequestStatus'

export interface TextbookStatusActionDetails {
  reason: string | null
  statusMessage: string | null
  externalConfirmed: boolean
  externalReference: string | null
}

interface Props {
  request: AdminTextbookRequestView
  nextStatus: Extract<TextbookRequestStatus, 'REQUESTED_TO_HQ' | 'OUTBOUND_REQUESTED' | 'SHIPPED' | 'CANCELLED' | 'REJECTED'>
  pending: boolean
  onClose: () => void
  onConfirm: (details: TextbookStatusActionDetails) => void
}

export function getTextbookStatusActionRequirements(
  currentStatus: TextbookRequestStatus,
  nextStatus: TextbookRequestStatus,
) {
  const requiresReason = nextStatus === 'REJECTED' || nextStatus === 'CANCELLED'
  const requiresStatusMessage = currentStatus === 'OUTBOUND_REQUESTED' && nextStatus === 'CANCELLED'
  return {
    requiresReason,
    requiresStatusMessage,
    requiresExternalConfirmation: requiresStatusMessage,
  }
}

export function TextbookStatusActionDialog({ request, nextStatus, pending, onClose, onConfirm }: Props) {
  const { requiresReason, requiresStatusMessage, requiresExternalConfirmation } =
    getTextbookStatusActionRequirements(request.status, nextStatus)
  const [reason, setReason] = useState('')
  const [statusMessage, setStatusMessage] = useState('')
  const [externalConfirmed, setExternalConfirmed] = useState(false)
  const [externalReference, setExternalReference] = useState('')
  const [submitted, setSubmitted] = useState(false)

  const reasonInvalid = submitted && requiresReason && !reason.trim()
  const statusMessageInvalid = submitted && requiresStatusMessage && !statusMessage.trim()
  const externalInvalid = submitted && requiresExternalConfirmation && !externalConfirmed

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setSubmitted(true)
    if (reasonInvalid || statusMessageInvalid || externalInvalid) return
    if (requiresReason && !reason.trim()) return
    if (requiresStatusMessage && !statusMessage.trim()) return
    if (requiresExternalConfirmation && !externalConfirmed) return
    onConfirm({
      reason: reason.trim() || null,
      statusMessage: statusMessage.trim() || null,
      externalConfirmed,
      externalReference: externalReference.trim() || null,
    })
  }

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !pending) onClose() }}>
      <DialogContent className="sm:max-w-lg" showCloseButton={!pending}>
        <form className="contents" onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>교재 요청 상태 변경 확인</DialogTitle>
            <DialogDescription>
              {request.organizationName ?? '센터'} · {request.learnerName ?? '학생'} · {textbookRequestBookLabel(request)}
            </DialogDescription>
          </DialogHeader>

          <Alert variant={nextStatus === 'REJECTED' || nextStatus === 'CANCELLED' ? 'destructive' : 'default'}>
            <AlertTriangle />
            <AlertTitle>
              {TEXTBOOK_STATUS_LABELS[request.status]} → {TEXTBOOK_STATUS_LABELS[nextStatus]}
            </AlertTitle>
            <AlertDescription>
              상태 변경은 감사 이력에 기록됩니다. 잘못 처리한 경우 Superadmin의 상태 이력에서 마지막 전이만 정정할 수 있습니다.
            </AlertDescription>
          </Alert>

          <FieldGroup>
            {requiresReason ? (
              <Field data-invalid={reasonInvalid || undefined}>
                <FieldLabel htmlFor="textbook-status-reason">운영 사유 *</FieldLabel>
                <Textarea
                  id="textbook-status-reason"
                  value={reason}
                  maxLength={1000}
                  disabled={pending}
                  aria-invalid={reasonInvalid || undefined}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder="상태를 변경하는 이유를 입력하세요."
                />
                {reasonInvalid ? <FieldError>운영 사유를 입력해 주세요.</FieldError> : null}
              </Field>
            ) : null}

            {requiresStatusMessage ? (
              <Field data-invalid={statusMessageInvalid || undefined}>
                <FieldLabel htmlFor="textbook-status-message">LC 노출 문구 *</FieldLabel>
                <Textarea
                  id="textbook-status-message"
                  value={statusMessage}
                  disabled={pending}
                  aria-invalid={statusMessageInvalid || undefined}
                  onChange={(event) => setStatusMessage(event.target.value)}
                  placeholder="예: 해피데이 출고가 취소되어 교재 요청을 종료했습니다."
                />
                <FieldDescription>원생 상세의 교재 요청 이력에 표시되며 최초 요청 메모는 보존됩니다.</FieldDescription>
                {statusMessageInvalid ? <FieldError>LC 노출 문구를 입력해 주세요.</FieldError> : null}
              </Field>
            ) : null}

            {requiresExternalConfirmation ? (
              <>
                <Field orientation="horizontal" data-invalid={externalInvalid || undefined}>
                  <Checkbox
                    id="textbook-external-confirmed"
                    checked={externalConfirmed}
                    disabled={pending}
                    aria-invalid={externalInvalid || undefined}
                    onCheckedChange={setExternalConfirmed}
                  />
                  <FieldContent>
                    <FieldLabel htmlFor="textbook-external-confirmed">
                      <FieldTitle>해피데이에서 실제 출고 취소 완료를 확인했습니다 *</FieldTitle>
                    </FieldLabel>
                    <FieldDescription>이 작업은 해피데이 취소 API를 호출하지 않습니다.</FieldDescription>
                    {externalInvalid ? <FieldError>외부 취소 완료 확인이 필요합니다.</FieldError> : null}
                  </FieldContent>
                </Field>
                <Field>
                  <FieldLabel htmlFor="textbook-external-reference">외부 확인 근거</FieldLabel>
                  <Input
                    id="textbook-external-reference"
                    value={externalReference}
                    maxLength={200}
                    disabled={pending}
                    onChange={(event) => setExternalReference(event.target.value)}
                    placeholder="해피데이 접수번호, 확인 메모 등"
                  />
                </Field>
              </>
            ) : null}
          </FieldGroup>

          <DialogFooter>
            <Button type="button" variant="outline" disabled={pending} onClick={onClose}>닫기</Button>
            <Button
              type="submit"
              variant={nextStatus === 'REJECTED' || nextStatus === 'CANCELLED' ? 'destructive' : 'default'}
              disabled={pending}
            >
              {pending ? <Spinner data-icon="inline-start" /> : <CheckCircle2 data-icon="inline-start" />}
              {pending ? '상태 변경 중' : '확인 후 상태 변경'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
